import type { RawOwnerGoalInput } from "../autonomy/goalUnderstanding";

import type { CyanGoalPlanningService } from "../autonomy/goalPlanningService";

import type { CyanGoalCapabilityService } from "../autonomy/goalCapabilityService";

import type { RepositorySweepService } from "../intake/repositorySweepService";

import type { ProjectDiagnosticService } from "../diagnostics/projectDiagnosticService";

import type { RepairPlanningService } from "../repair/repairPlanningService";

import type { CyanProgramService } from "./cyanProgramService";

import type { MultiAgentScheduler } from "./multiAgentScheduler";

import type { MissionProgramAuthority } from "../../missionControl";

import { randomId } from "../../shared/randomId";

import { repairProposalToWorkPackage } from "./repairWorkPackageMapper";

import type {
  OperationalTakeoverActivation,
  OperationalTakeoverPreparation,
} from "./operationalTakeoverTypes";

export interface PrepareOperationalTakeoverInput {
  readonly ownerGoal: RawOwnerGoalInput;

  readonly objective: string;
}

export interface ActivateOperationalTakeoverInput {
  readonly preparation: OperationalTakeoverPreparation;

  readonly requestedBy: string;

  readonly conversationId?: string;

  readonly projectId?: string;

  readonly repository?: string;

  readonly branch?: string;

  readonly headSha?: string;
}

/**
 * Cyan understands, diagnoses, plans and delegates.
 *
 * Cyan itself never becomes the terminal/filesystem/Git/
 * database/infrastructure/production executor.
 *
 * Execution belongs to specialized governed Agents.
 */
export class CyanOperationalTakeoverService {
  constructor(
    private readonly planning: CyanGoalPlanningService,

    private readonly capabilities: CyanGoalCapabilityService,

    private readonly sweep: RepositorySweepService,

    private readonly diagnostics: ProjectDiagnosticService,

    private readonly repairPlanning: RepairPlanningService,

    private readonly programs: CyanProgramService,

    private readonly scheduler: MultiAgentScheduler,

    private readonly missionAuthority: MissionProgramAuthority,
  ) {}

  async prepare(
    input: PrepareOperationalTakeoverInput,
  ): Promise<OperationalTakeoverPreparation> {
    const goalPlan = this.planning.createPlan(input.ownerGoal);

    const capabilities = this.capabilities.resolvePlan(goalPlan);

    const projectEvidence = await this.sweep.sweep();

    const diagnostic = this.diagnostics.diagnose(projectEvidence);

    const repairPlan = this.repairPlanning.createPlan(diagnostic.matrix);

    const approvalRequired = repairPlan.proposals
      .filter((proposal) => proposal.requiresApproval)
      .map((proposal) => proposal.id);

    const blocked = repairPlan.proposals
      .filter(
        (proposal) =>
          proposal.status === "blocked" ||
          proposal.authority === "execute_privileged" ||
          !proposal.reversible,
      )
      .map((proposal) => proposal.id);

    const workPackages = repairPlan.proposals
      .filter(
        (proposal) =>
          proposal.status === "ready" &&
          proposal.authority === "execute_reversible" &&
          proposal.reversible &&
          !proposal.requiresApproval,
      )
      .map(repairProposalToWorkPackage);

    const state =
      goalPlan.goal.status === "understanding"
        ? "NEEDS_CLARIFICATION"
        : approvalRequired.length || blocked.length
          ? "WAITING_FOR_OWNER"
          : "READY";

    return {
      objective: input.objective,

      goalPlan,

      capabilities,

      projectEvidence,

      diagnosticMatrix: diagnostic.matrix,

      checklist: diagnostic.checklist,

      repairPlan,

      workPackages,

      approvalRequired,

      blocked,

      state,
    };
  }

  async activate(
    input: ActivateOperationalTakeoverInput,
  ): Promise<OperationalTakeoverActivation> {
    if (input.preparation.state === "NEEDS_CLARIFICATION") {
      throw new Error("Operational takeover requires goal clarification.");
    }

    if (!input.preparation.workPackages.length) {
      throw new Error("No approved reversible work is ready for execution.");
    }

    const missionId =
      randomId();

    await this.missionAuthority.authorize({
      missionId,

      preparation:
        input.preparation,

      requestedBy:
        input.requestedBy,

      conversationId:
        input.conversationId,

      projectId:
        input.projectId,

      repository:
        input.repository,
    });

    const program = await this.programs.launch({
      missionId,

      objective: input.preparation.objective,

      requestedBy: input.requestedBy,

      workPackages: input.preparation.workPackages,

      conversationId: input.conversationId,

      projectId: input.projectId,

      repository: input.repository,

      branch: input.branch,

      headSha: input.headSha,
    });

    const scheduled = await this.scheduler.tick(program.id);

    return {
      preparation: input.preparation,

      program: scheduled,

      state:
        scheduled.state === "GREEN"
          ? "GREEN"
          : scheduled.state === "BLOCKED"
            ? "BLOCKED"
            : "RUNNING",
    };
  }
}
